import { Button, Tag } from '@ads/components-react';

import {
  AcademicCapIcon,
  DocumentTextIcon,
  GlobeAltIcon,
  ListBulletIcon,
  PencilSquareIcon,
  RocketLaunchIcon,
} from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

import type {
  DropDownListViewModel,
  DropDownRepositoryResponse,
  ExamLangListItemViewModel,
} from '@/shared/api/generated';
import type { SelectableQuestion } from '../../../hooks/useExamQuestionSelection';
import type { ExamFormData, LessonPlan } from '../../../types/exam';

// ─── Tipos ────────────────────────────────────────────────────────────────────

/** Props do ExamReviewStep */
export interface ExamReviewStepProps {
  readonly formData: ExamFormData;
  readonly examLangs: ExamLangListItemViewModel[];
  readonly selectedQuestions: SelectableQuestion[];
  readonly linkedPlans: LessonPlan[];
  readonly examTypes: DropDownRepositoryResponse[];
  readonly gradeCriteriaOptions: DropDownListViewModel[];
  readonly onGoToStep: (step: number) => void;
}

// ─── Sub-componente: cabeçalho de card ───────────────────────────────────────

interface ReviewCardHeaderProps {
  readonly icon: React.ElementType;
  readonly iconBg: string;
  readonly iconColor: string;
  readonly title: string;
  readonly subtitle: string;
  readonly onEdit: () => void;
  readonly editLabel: string;
}

function ReviewCardHeader({
  icon: Icon,
  iconBg,
  iconColor,
  title,
  subtitle,
  onEdit,
  editLabel,
}: ReviewCardHeaderProps) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-mini">
        <div className={`flex size-md items-center justify-center rounded-md ${iconBg}`}>
          <Icon className={`size-xxs ${iconColor}`} />
        </div>
        <div className="flex flex-col gap-quark">
          <span className="ts-subtitle-xs text-ctx-content-title">{title}</span>
          <span className="ts-paragraph-xxs text-ctx-content-base">{subtitle}</span>
        </div>
      </div>
      <Button hierarchy="secondary" size="md" leadingIcon={PencilSquareIcon} onClick={onEdit}>
        {editLabel}
      </Button>
    </div>
  );
}

// ─── Sub-componente: linha de campo ──────────────────────────────────────────

function FieldRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <>
      <div className="flex items-center gap-nano">
        <span className="ts-paragraph-xxs text-ctx-content-title">{label}</span>
      </div>
      <span className="ts-paragraph-xxs !font-medium text-ctx-content-base">{value || '—'}</span>
    </>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────

/**
 * Step de revisão final do wizard de criação de avaliação.
 *
 * Exibe cards de resumo para Dados Gerais, Idiomas, Questões e Publicações,
 * cada um com botão de editar. Inclui toggle de ativação no final.
 */
export function ExamReviewStep({
  formData,
  examLangs,
  selectedQuestions,
  linkedPlans,
  examTypes,
  gradeCriteriaOptions,
  onGoToStep,
}: ExamReviewStepProps) {
  const { t } = useTranslation('examManagement');

  const examTypeName = examTypes.find(o => o.key === formData.examType)?.value ?? '—';
  const gradeCriteriaName =
    gradeCriteriaOptions.find(o => o.key === formData.gradeCriteria)?.value ?? '—';

  const LangFlag: Record<string, string> = {
    'pt-BR': 'BR',
    'en-US': 'ES',
    'es-ES': 'ES',
    'fr-FR': 'FR',
  };

  const LangLabel: Record<string, string> = {
    'pt-BR': 'Português (BR)',
    'en-US': 'English (US)',
    'es-ES': 'Español (ES)',
    'fr-FR': 'Français (FR)',
  };

  return (
    <div className="flex flex-col gap-sm">
      {/* ── Dados Gerais ─────────────────────────────────────────────────── */}
      <div className="overflow-hidden rounded-md border-hairline border-ctx-layout-border bg-ctx-layout-surface shadow-level1">
        <div className="px-sm pt-sm pb-mini">
          <ReviewCardHeader
            icon={DocumentTextIcon}
            iconBg="bg-base-accent-100"
            iconColor="text-base-accent-500"
            title={t('review.sections.general')}
            subtitle={t('review.sections.generalSubtitle')}
            onEdit={() => onGoToStep(1)}
            editLabel={t('review.edit')}
          />
        </div>

        <div className="border-t-hairline border-ctx-layout-border px-sm py-mini">
          <div className="grid grid-cols-2 gap-y-xs gap-x-xxs lg:grid-cols-4">
            <FieldRow label={t('review.fields.title')} value={formData.title} />
            <FieldRow label={t('review.fields.criteria')} value={gradeCriteriaName} />
            <FieldRow label={t('review.fields.type')} value={examTypeName} />
            <FieldRow label={t('review.fields.weight')} value={formData.weight} />
            <FieldRow
              label={t('review.fields.discipline')}
              value={
                formData.disciplineName ? (
                  <span className="flex items-center gap-nano !line-clamp-2">
                    {formData.disciplineName}
                  </span>
                ) : (
                  '—'
                )
              }
            />
            <FieldRow
              label={t('review.fields.randomize')}
              value={
                formData.randomize ? (
                  <div className="flex items-center w-full h-full">
                    <Tag
                      label={t('review.fields.yes')}
                      status="positive"
                      size="md"
                      custom={{ text: 'text-nowrap' }}
                    />
                  </div>
                ) : (
                  <span className="ts-paragraph-xs text-ctx-content-title">
                    {t('review.fields.no')}
                  </span>
                )
              }
            />
          </div>
        </div>
      </div>

      {/* ── Idiomas ──────────────────────────────────────────────────────── */}
      <div className="overflow-hidden rounded-md border-hairline border-ctx-layout-border bg-ctx-layout-surface shadow-level1">
        <div className="px-sm pt-sm pb-mini">
          <ReviewCardHeader
            icon={GlobeAltIcon}
            iconBg="bg-base-accent-100"
            iconColor="text-base-accent-500"
            title={t('review.sections.languages')}
            subtitle={t('review.sections.languagesSubtitle')}
            onEdit={() => onGoToStep(2)}
            editLabel={t('review.edit')}
          />
        </div>

        <div className="border-t-hairline border-ctx-layout-border px-sm py-mini">
          {examLangs.length === 0 ? (
            <span className="ts-paragraph-xs text-ctx-content-title">
              {t('languages.minRequired')}
            </span>
          ) : (
            <div className="flex flex-wrap gap-xs">
              {examLangs.map((lang, idx) => (
                <div
                  key={lang.id ?? idx}
                  className="flex items-center gap-nano text-ctx-content-base rounded-pill border-hairline border-ctx-layout-border bg-ctx-layout-body px-xs py-quark"
                >
                  <span className="ts-subtitle-xxs">
                    {LangFlag[lang.cultureInfo ?? ''] ?? '🌐'}
                  </span>
                  <span className="ts-subtitle-xxs">
                    {LangLabel[lang.cultureInfo ?? ''] ?? lang.cultureInfo}
                  </span>
                  {idx === 0 && (
                    <span className="ts-caption-xxs">· {t('languages.form.active')}</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Questões da Avaliação ─────────────────────────────────────────── */}
      <div className="overflow-hidden rounded-md border-hairline border-ctx-layout-border bg-ctx-layout-surface shadow-level1">
        <div className="px-sm pt-sm pb-mini">
          <ReviewCardHeader
            icon={ListBulletIcon}
            iconBg="bg-base-accent-100"
            iconColor="text-base-accent-500"
            title={t('review.sections.questions')}
            subtitle={t('review.sections.questionsSubtitle', {
              count: selectedQuestions.length,
            })}
            onEdit={() => onGoToStep(3)}
            editLabel={t('review.edit')}
          />
        </div>

        <div className="border-t-hairline border-ctx-layout-border px-sm py-mini">
          {selectedQuestions.length === 0 ? (
            <span className="ts-paragraph-xs text-ctx-content-base">
              {t('review.alerts.noQuestions')}
            </span>
          ) : (
            <div className="flex flex-col gap-sm">
              {/* Questões agrupadas por idioma */}
              {Object.entries(
                selectedQuestions.reduce<Record<string, typeof selectedQuestions>>(
                  (acc, q) => ({
                    ...acc,
                    [q.cultureInfo]: [...(acc[q.cultureInfo] ?? []), q],
                  }),
                  {},
                ),
              ).map(([culture, questions]) => (
                <div key={culture} className="flex flex-col gap-nano">
                  <div className="flex items-center gap-nano">
                    <span className="ts-subtitle-xxs text-ctx-content-title">
                      {LangLabel[culture] ?? culture}
                    </span>
                    <span className="rounded-pill bg-ctx-layout-border px-quark ts-caption-xxs text-ctx-content-base">
                      {questions.length}
                    </span>
                  </div>
                  {questions.map((q, idx) => (
                    <div
                      key={q.id}
                      className="flex items-center gap-xs rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-body px-xs py-xxs"
                    >
                      <span className="flex size-md shrink-0 items-center justify-center rounded-sm bg-ctx-layout-surface ts-subtitle-xxs text-ctx-content-base shadow-level1">
                        {String(idx + 1).padStart(2, '0')}
                      </span>
                      <div className="min-w-none flex-1">
                        <p
                          className="truncate ts-paragraph-xs text-ctx-content-title"
                          dangerouslySetInnerHTML={{ __html: q.text }}
                        />
                        <p className="ts-caption-xxs text-ctx-content-base">{q.type}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Publicações em Aulas ─────────────────────────────────────────── */}
      <div className="overflow-hidden rounded-md border-hairline border-ctx-layout-border bg-ctx-layout-surface shadow-level1">
        <div className="px-sm pt-sm pb-mini">
          <ReviewCardHeader
            icon={RocketLaunchIcon}
            iconBg="bg-base-accent-100"
            iconColor="text-base-accent-500"
            title={t('review.sections.publications')}
            subtitle={t('review.sections.publicationsSubtitle')}
            onEdit={() => onGoToStep(0)}
            editLabel={t('review.edit')}
          />
        </div>

        <div className="border-t-hairline border-ctx-layout-border px-sm py-mini">
          {linkedPlans.length === 0 ? (
            <div className="flex items-start justify-between gap-sm rounded-sm border-hairline border-base-warning-200 bg-base-warning-50 p-xs">
              <div className="flex items-start gap-xs">
                <AcademicCapIcon className="mt-quark size-xs shrink-0 text-base-warning-500" />
                <div className="flex flex-col gap-quark">
                  <span className="ts-subtitle-xxs text-base-warning-700">
                    {t('review.publications.empty')}
                  </span>
                  <span className="ts-paragraph-xxs text-base-warning-600">
                    {t('review.publications.emptyHint')}
                  </span>
                </div>
              </div>
              <Button hierarchy="secondary" size="md" onClick={() => onGoToStep(0)}>
                {t('review.publications.linkLesson')}
              </Button>
            </div>
          ) : (
            <div className="flex flex-col gap-nano">
              {linkedPlans.map(plan => (
                <div
                  key={plan.id}
                  className="flex items-center justify-between rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-body px-xs py-xxs"
                >
                  <span className="ts-paragraph-xs text-ctx-content-title">{plan.title}</span>
                  <span className="ts-caption-xxs text-ctx-content-title">{plan.classGroup}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
